/**
 * "FP users - last active" dashboard card (staff-only).
 *
 * Shows who uses this FP's inspector and when they were last active on it,
 * from /api/inspector/{fp}/engagement (user-activity feature). Hidden for
 * FP_USER viewers - franchisees must not see who else views their FP; the
 * backend enforces the same boundary, this just avoids a guaranteed 403.
 */

import React from 'react'
import { Box, Paper, Skeleton, Stack, Typography } from '@mui/material'
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline'
import { useAuth } from '../../../contexts/AuthContext'
import { useDataFetching } from '../../../hooks/useDataFetching'
import { activityApi, FpEngagementResponse } from '../../../services/activityApi'
import RelativeTime from '../../shared/RelativeTime'
import { getRoleDisplayName } from '../../../utils/roleDisplay'
import { poolwerxColors } from '../../../theme/colors'

const MAX_ROWS = 6

interface FpEngagementCardProps {
  fpCode: string
}

export const FpEngagementCard: React.FC<FpEngagementCardProps> = ({ fpCode }) => {
  const { user } = useAuth()
  const isStaff = Boolean(user && user.role !== 'FP_USER')

  const { data, loading } = useDataFetching<FpEngagementResponse | null>({
    fetchFn: async () => (isStaff ? activityApi.getFpEngagement(fpCode) : null),
    dependencies: [fpCode, isStaff],
  })

  if (!isStaff) return null

  const users = (data?.users || []).slice(0, MAX_ROWS)

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <PeopleOutlineIcon sx={{ color: poolwerxColors.teal }} fontSize="small" />
        <Typography variant="subtitle2">FP users - last active</Typography>
      </Stack>

      {loading && (
        <Stack spacing={1}>
          <Skeleton height={28} />
          <Skeleton height={28} />
        </Stack>
      )}

      {!loading && users.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No users assigned or active on this FP yet.
        </Typography>
      )}

      {!loading && users.length > 0 && (
        <Stack spacing={1}>
          {users.map((row) => {
            return (
              <Stack
                key={row.user_id}
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="space-between"
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" noWrap>
                    {row.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap component="div">
                    {row.role === 'FP_USER' ? row.email : getRoleDisplayName(row.role)}
                  </Typography>
                </Box>
                <Box sx={{ whiteSpace: 'nowrap' }}>
                  <RelativeTime value={row.last_active_on_fp} variant="caption" />
                </Box>
              </Stack>
            )
          })}
        </Stack>
      )}
    </Paper>
  )
}

export default FpEngagementCard
